import { apiRequest } from "@/utils/api";
import { TeamMember } from "@/types";

// Define the API endpoint base
const TEAMS_ENDPOINT = "/teams";

// Interface for team creation/update
export interface TeamData {
  name: string;
  website?: string;
  logo_url?: string;
}

// Interface for adding a team member
export interface AddTeamMemberData {
  email: string;
  role: string;
}

/**
 * Create a new team
 */
export async function createTeam(teamData: TeamData): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(TEAMS_ENDPOINT, {
    method: "POST",
    body: teamData,
  });
}

/**
 * Update an existing team
 */
export async function updateTeam(
  teamId: string,
  teamData: TeamData
): Promise<void> {
  return apiRequest<void>(`${TEAMS_ENDPOINT}/${teamId}`, {
    method: "PATCH",
    body: teamData,
  });
}

/**
 * Delete a team
 */
export async function deleteTeam(teamId: string): Promise<void> {
  return apiRequest<void>(`${TEAMS_ENDPOINT}/${teamId}`, {
    method: "DELETE",
  });
}

/**
 * Get all members of a team
 */
export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  return apiRequest<TeamMember[]>(`${TEAMS_ENDPOINT}/${teamId}/members`);
}

/**
 * Add a new member to a team
 */
export async function addTeamMember(
  teamId: string,
  memberData: AddTeamMemberData
): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(`${TEAMS_ENDPOINT}/${teamId}/members`, {
    method: "POST",
    body: memberData,
  });
}

/**
 * Remove a member from a team
 */
export async function removeTeamMember(
  teamId: string,
  memberId: string
): Promise<void> {
  return apiRequest<void>(`${TEAMS_ENDPOINT}/${teamId}/members/${memberId}`, {
    method: "DELETE",
  });
}

/**
 * Update a team member's role
 */
export async function updateTeamMemberRole(
  teamId: string,
  memberId: string,
  role: string
): Promise<void> {
  return apiRequest<void>(`${TEAMS_ENDPOINT}/${teamId}/members/${memberId}`, {
    method: "PATCH",
    body: { role },
  });
}

/**
 * Upload a team logo
 */
export async function uploadTeamLogo(
  teamId: string,
  file: File
): Promise<{ logo_url: string }> {
  const formData = new FormData();
  formData.append("logo", file);

  return apiRequest<{ logo_url: string }>(`${TEAMS_ENDPOINT}/${teamId}/logo`, {
    method: "POST",
    body: formData,
    headers: {}, // Let the browser set the content-type with boundary for FormData
  });
}
